import React, { useState, useEffect, useRef } from 'react';
import { useGame } from '../context/GameContext';
import { TREASURE_OUTCOMES } from '../game/boons';

// v3.16: Chest reward reveal animation — shows a diff of player stats before/after
// so players can visually confirm their permanent boosts actually applied.
function StatDiff({ label, before, after, suffix = '' }) {
  if (before === after) return null;
  const delta = after - before;
  const isBoost = delta > 0;
  return (
    <div
      className="chest-stat-diff flex items-center gap-3 px-4 py-2 rounded-md bg-black/40 border-2"
      style={{ borderColor: isBoost ? 'var(--neon)' : 'var(--pink)' }}
      data-testid={`chest-diff-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">{label}</span>
      <span className="font-mono text-sm text-[color:var(--text-dim)]">{before}{suffix}</span>
      <span className="text-[color:var(--text-dim)]">→</span>
      <span
        className="font-display font-black text-xl"
        style={{ color: isBoost ? 'var(--neon)' : 'var(--pink)' }}
      >
        {after}{suffix}
      </span>
      <span
        className="ml-auto font-mono font-bold text-sm"
        style={{ color: isBoost ? 'var(--neon)' : 'var(--pink)' }}
      >
        {isBoost ? '+' : ''}{delta}{suffix}
      </span>
    </div>
  );
}

function snapshotPlayer(run) {
  if (!run || !run.player) return null;
  const p = run.player;
  return {
    hp: p.hp || 0,
    maxHp: p.maxHp || 0,
    gold: p.gold || 0,
    deckLen: (p.deck || []).length,
    relicsLen: (p.relics || []).length,
    permaStrength: p._permaStrength || 0,
    permaDamageMult: p._permaDamageMult || 1,
  };
}

export default function Treasure() {
  const { pendingTreasure, openTreasure, run } = useGame();
  const [result, setResult] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const beforeRef = useRef(null);
  useEffect(() => { if (run && !beforeRef.current) beforeRef.current = snapshotPlayer(run); }, [run]);
  // Re-snapshot when run updates after openTreasure has been called.
  useEffect(() => {
    if (result && run) setSnapshot(snapshotPlayer(run));
  }, [run, result]);
  if (!pendingTreasure) return null;
  const chestIdxs = pendingTreasure.chestIdxs || [0, 1, 2];
  const chestsToShow = chestIdxs.map(i => ({ ...TREASURE_OUTCOMES[i], originalIdx: i }));
  const pick = async (originalIdx) => {
    if (!beforeRef.current) beforeRef.current = snapshotPlayer(run);
    const msg = await openTreasure(originalIdx);
    setResult(msg);
    // Delay snapshot so React commits the setRun before we diff.
    setTimeout(() => setSnapshot(snapshotPlayer(run)), 20);
  };

  const before = beforeRef.current;
  const after = snapshot;

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-6" data-testid="treasure-screen">
      <div className="text-xs uppercase tracking-[0.4em] text-[color:var(--text-dim)]">◆ TREASURE VAULT ◆</div>
      <h1 className="font-display text-5xl font-black uppercase text-[color:var(--pink)] mt-2">PICK A CHEST</h1>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 w-full max-w-4xl">
        {chestsToShow.map((t, displayIdx) => (
          <button key={t.key} className="brutal-btn brutal-btn-pink flex flex-col items-center gap-3 p-8"
            disabled={!!result}
            onClick={() => pick(t.originalIdx)}
            data-testid={`treasure-chest-${displayIdx}`}>
            <div className="text-7xl">{t.art}</div>
            <div className="font-display text-lg text-[color:var(--pink)]">{t.label}</div>
          </button>
        ))}
      </div>
      {result && (
        <div className="mt-10 text-center w-full max-w-2xl chest-result-reveal" data-testid="treasure-result">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">RESULT</div>
          <div className="mt-2 font-display text-3xl font-black uppercase text-[color:var(--neon)]">{result}</div>
          {before && after && (
            <div className="mt-6 flex flex-col gap-2 text-left" data-testid="chest-stat-changes">
              <StatDiff label="HP" before={before.hp} after={after.hp} />
              <StatDiff label="MAX HP" before={before.maxHp} after={after.maxHp} />
              <StatDiff label="GOLD" before={before.gold} after={after.gold} />
              <StatDiff label="DECK SIZE" before={before.deckLen} after={after.deckLen} />
              <StatDiff label="RELICS" before={before.relicsLen} after={after.relicsLen} />
              <StatDiff label="PERMA STR" before={before.permaStrength} after={after.permaStrength} />
              {before.permaDamageMult !== after.permaDamageMult && (
                <div
                  className="chest-stat-diff flex items-center gap-3 px-4 py-2 rounded-md bg-black/40 border-2"
                  style={{ borderColor: 'var(--neon)' }}
                  data-testid="chest-diff-perma-dmg-mult"
                >
                  <span className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">PERMA DMG</span>
                  <span className="font-mono text-sm text-[color:var(--text-dim)]">{Math.round((before.permaDamageMult) * 100)}%</span>
                  <span className="text-[color:var(--text-dim)]">→</span>
                  <span className="font-display font-black text-xl" style={{ color: 'var(--neon)' }}>{Math.round(after.permaDamageMult * 100)}%</span>
                  <span className="ml-auto font-mono font-bold text-sm" style={{ color: 'var(--neon)' }}>+{Math.round((after.permaDamageMult - before.permaDamageMult) * 100)}%</span>
                </div>
              )}
            </div>
          )}
          <button className="brutal-btn mt-6" onClick={() => openTreasure(null)} data-testid="treasure-continue">CONTINUE</button>
        </div>
      )}
    </div>
  );
}
